import { Link } from "react-router";
import { ExternalLink, TrendingUp } from "lucide-react";
import { formatCurrencyGbpCompact } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import type * as API from "@shared-core/data/api-types";
import { Card } from "../../primitives/card";
import { QuoteVolumeChart } from "./QuoteVolumeChart";

type PipelineHero = NonNullable<API.DashboardHome["pipeline"]>;

// Dashboard hero: win-rate-weighted forecast pipeline (from the 02:30 projection)
// and the live 30-day quotes-won ready-reckoner as a stats rail, beside a live
// 30-day quote-volume chart with the daily pace needed keyed on a dashed line.
// Clicking through goes to the estimates list — the quotes behind the number.
// The open-quotes → pipeline drill (age bands + win-rate curve) lives in /plan.
// Mixed freshness (pipeline is the morning snapshot; rate + quotes are live), so
// no "as of" stamp on the tile. Every value degrades to "—" / no line when the
// backend returns null (no projection today, no target, plateau 0).
export function PipelineHeroCard({ pipeline }: { pipeline?: PipelineHero }) {
  const weighted = pipeline?.weighted_pipeline_amt ?? null;
  const wonPct = pipeline?.quotes_won_30d?.pct ?? null;
  const pace = pipeline?.daily_quote_pace_amt ?? null;
  const points = (pipeline?.daily_quoted_last_30 ?? []).map((p) => ({
    date: p.date,
    amt: p.contribution_amt,
  }));

  return (
    <Link to={paths.estimates()} className="block h-full">
      <Card className="p-4 h-full flex flex-col hover:bg-muted/50 transition-colors cursor-pointer">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <TrendingUp className="size-4" />
            Pipeline
          </div>
          <div className="text-xs text-primary inline-flex items-center gap-1">
            View quotes
            <ExternalLink className="size-3" />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Two KPIs in a row — dominant headline left, quiet secondary right */}
          <div className="sm:w-[45%] shrink-0 flex items-baseline justify-between gap-4 mb-2 sm:mb-0">
            <div>
              <div className="text-3xl font-bold text-foreground tracking-tight">
                {weighted == null ? "—" : formatCurrencyGbpCompact(weighted)}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                win-rate weighted
              </div>
            </div>
            <div className="text-right">
              <div className="text-xl font-semibold text-muted-foreground tabular-nums">
                {wonPct == null ? "—" : `${Math.round(wonPct)}%`}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                of quotes won, 30d
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-xs text-muted-foreground mb-1.5">
              Quotes raised, last 30 days
              {pace != null && (
                <> (target pace {formatCurrencyGbpCompact(pace)}/day)</>
              )}
            </div>
            <QuoteVolumeChart points={points} paceGbp={pace} />
          </div>
        </div>
      </Card>
    </Link>
  );
}
